import { X } from 'lucide-react'
import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useId,
  useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../cn'
import { type CloseReason, useModalOverlay } from '../overlay'
import { Button, type ButtonProps } from './Button'

export interface DialogProps {
  open: boolean
  /**
   * The single close path (P-UI-004): the X button, Escape, Cancel (`<DialogCancel>`) and a
   * backdrop click all call this, with the reason.
   */
  onClose: (reason: CloseReason) => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  /** Footer actions, usually `<DialogCancel />` plus a primary `<Button>`. */
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** Element to focus on open (defaults to the first focusable element). */
  initialFocusRef?: RefObject<HTMLElement | null>
  /** Backdrop click closes (default true). */
  closeOnBackdrop?: boolean
  className?: string
}

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
} as const

const DialogCloseContext = createContext<((reason: CloseReason) => void) | null>(null)

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  initialFocusRef,
  closeOnBackdrop = true,
  className,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descriptionId = useId()
  const close = useCallback((reason: CloseReason) => onClose(reason), [onClose])
  const onEscape = useCallback(() => close('escape'), [close])

  useModalOverlay({ open, panelRef, hostRef, onEscape, initialFocusRef })

  if (!open) return null

  return createPortal(
    <div
      ref={hostRef}
      className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6"
    >
      <div
        aria-hidden="true"
        data-testid="dialog-backdrop"
        className="absolute inset-0 animate-fade-in bg-[#17312e]/40 backdrop-blur-[2px]"
        onClick={closeOnBackdrop ? () => close('backdrop') : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'relative flex max-h-[92dvh] w-full animate-pop-in flex-col overflow-hidden rounded-t-xl border border-line bg-surface shadow-pop focus:outline-none sm:rounded-xl',
          SIZES[size],
          className,
        )}
      >
        <DialogCloseContext value={close}>
          <header className="flex items-start justify-between gap-4 border-b border-line px-6 pt-5 pb-4">
            <div className="min-w-0">
              <h2 id={titleId} className="font-display text-xl font-semibold leading-snug text-ink">
                {title}
              </h2>
              {description ? (
                <p id={descriptionId} className="mt-1 text-sm leading-relaxed text-sage">
                  {description}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => close('close-button')}
              aria-label="Close dialog"
              className="-mt-1 -mr-2 inline-flex size-9 shrink-0 items-center justify-center rounded-md text-sage hover:bg-wash hover:text-ink"
            >
              <X aria-hidden="true" className="size-5" />
            </button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer ? (
            <footer className="flex flex-col-reverse gap-2.5 border-t border-line bg-cream/60 px-6 py-4 sm:flex-row sm:justify-end">
              {footer}
            </footer>
          ) : null}
        </DialogCloseContext>
      </div>
    </div>,
    document.body,
  )
}

/** Cancel button wired to the dialog's single close path. */
export function DialogCancel({ children = 'Cancel', ...rest }: Omit<ButtonProps, 'onClick'>) {
  const close = useContext(DialogCloseContext)
  return (
    <Button variant="secondary" onClick={() => close?.('cancel')} {...rest}>
      {children}
    </Button>
  )
}
